import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, excelDate, seededRandom } from "../spec";

const SHEET = "LG Cobranza";
const TABLE = "Facturas";
const CORTE = "2026-03-31";
const TASA = 0.03;

const CLIENTES = [
  "Grupo Altamira", "Comercial Norte", "Distribuidora Luna", "Ferretería El Clavo",
  "Hotel Mirador", "Farmacias Vida", "Constructora Roble", "Café La Esquina",
  "Papelería Central", "Transportes Águila",
] as const;

const rnd = seededRandom(2401);
const ROWS = Array.from({ length: 30 }, (_, i) => {
  const day = rnd.int(0, 118); // vencimientos del 1 ene al 29 abr
  const venc = excelDate("2026-01-01") + day;
  return [
    `F-${2401 + i}`,
    rnd.pick(CLIENTES),
    rnd.round(3000, 80000, 50),
    venc,
    rnd.next() < 0.4 ? "Sí" : "No",
  ] as [string, string, number, number, string];
});

const corte = excelDate(CORTE);
const dias = (venc: number) => (corte > venc ? corte - venc : 0);

export const spec: FuncionProjectSpec = {
  projectId: "funciones-si",
  sheets: [
    {
      name: SHEET,
      title: "Cuentas por cobrar",
      subtitle: "Facturas emitidas en el primer cuatrimestre — escribe tus fórmulas en las columnas amarillas",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Factura", "Cliente", "Monto", "Vencimiento", "Pagada"],
          rows: ROWS,
          answerColumns: [
            "Estado",
            "Descuento",
            "Días vencida",
            "Antigüedad",
            "Acción",
            "Interés moratorio",
          ],
          formats: {
            Monto: "$#,##0",
            Vencimiento: "dd/mm/yyyy",
            Descuento: "$#,##0.00",
            "Interés moratorio": "$#,##0.00",
          },
        },
      ],
      inputs: [
        { address: "N4", label: "Fecha de corte", value: corte, format: "dd/mm/yyyy" },
        { address: "N5", label: "Tasa moratoria mensual", value: TASA, format: "0%" },
      ],
    },
  ],
  checks: {
    1: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Estado", fns: ["IF"],
      expected: ROWS.map((r) => (r[4] === "Sí" ? "Cobrada" : "Pendiente")),
    },
    2: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Descuento", fns: ["IF"],
      expected: ROWS.map((r) => (r[2] >= 20000 ? r[2] * 0.05 : 0)),
    },
    3: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Días vencida", fns: ["IF"],
      expected: ROWS.map((r) => dias(r[3])),
    },
    4: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Antigüedad", fns: ["IF"],
      expected: ROWS.map((r) => {
        const d = dias(r[3]);
        if (d === 0) return "Al corriente";
        if (d <= 30) return "1-30 días";
        if (d <= 60) return "31-60 días";
        return "Más de 60 días";
      }),
    },
    5: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Acción", fns: ["IF"],
      expected: ROWS.map((r) => {
        const d = dias(r[3]);
        if (r[4] === "Sí") return "";
        if (d > 60) return "Enviar a jurídico";
        if (d > 0) return "Llamar al cliente";
        return "Enviar recordatorio";
      }),
    },
    6: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Interés moratorio", fns: ["IF"],
      expected: ROWS.map((r) => (r[4] === "No" ? (r[2] * TASA * dias(r[3])) / 30 : 0)),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-si",
  title: "SI: Cuentas por cobrar",
  subtitle: "Decisiones de una condición y SI anidados",
  description:
    "Eres analista de cobranza. Tienes 30 facturas y necesitas saber cuáles están cobradas, cuánto descuento aplica, cuántos días lleva vencida cada una y qué acción tomar.\n\nTodo se resuelve con la función SI: una prueba lógica, qué hacer si se cumple y qué hacer si no.",
  topics: [
    "SI con texto, números y fechas",
    "Referencias absolutas a celdas de parámetros",
    "SI anidados para clasificar en rangos",
    "Devolver texto vacío \"\"",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: ¿Cobrada o pendiente?",
      description:
        "En la hoja <strong>LG Cobranza</strong>, columna <strong>Estado</strong> (F5 hacia abajo): si la factura está <strong>Pagada</strong> (\"Sí\") escribe <strong>Cobrada</strong>; si no, <strong>Pendiente</strong>.",
      solution: [
        `Selecciona ${F("F5")}.`,
        `La prueba lógica compara texto: ${F('[@Pagada]="Sí"')}.`,
        `Escribe ${F('=SI([@Pagada]="Sí","Cobrada","Pendiente")')} y presiona ${F("Enter")}. La tabla copia la fórmula a toda la columna.`,
      ],
      expectedResult: "Cobrada / Pendiente según la columna Pagada",
      finalFormula: '=SI([@Pagada]="Sí","Cobrada","Pendiente")',
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Descuento por volumen",
      description:
        "En la columna <strong>Descuento</strong>: las facturas de <strong>$20,000 o más</strong> reciben un <strong>5%</strong> del monto; las demás, <strong>0</strong>.",
      solution: [
        `Selecciona ${F("G5")}.`,
        `La prueba es ${F("[@Monto]>=20000")}. Si se cumple, calcula ${F("[@Monto]*5%")}; si no, devuelve ${F("0")}.`,
        `Escribe ${F("=SI([@Monto]>=20000,[@Monto]*5%,0)")}.`,
      ],
      expectedResult: "5% del monto o 0",
      finalFormula: "=SI([@Monto]>=20000,[@Monto]*5%,0)",
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: Días vencida",
      description:
        "En la columna <strong>Días vencida</strong>: si la <strong>fecha de corte</strong> (celda <strong>N4</strong>) es posterior al <strong>Vencimiento</strong>, calcula los días de diferencia; si no, <strong>0</strong>. Usa <strong>$N$4</strong> para que la referencia no se mueva.",
      solution: [
        `Selecciona ${F("H5")}.`,
        "Las fechas son números, así que se pueden comparar y restar.",
        `Escribe ${F("=SI($N$4>[@Vencimiento],$N$4-[@Vencimiento],0)")}.`,
        "Si ves una fecha en lugar de un número, cambia el formato de la columna a Número.",
      ],
      expectedResult: "Días entre el vencimiento y el 31/03/2026, o 0",
      finalFormula: "=SI($N$4>[@Vencimiento],$N$4-[@Vencimiento],0)",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Antigüedad de la cartera",
      description:
        conditions(
        "En la columna <strong>Antigüedad</strong>, clasifica cada factura según sus <strong>Días vencida</strong>:",
        [
          "<strong>0</strong> días → <strong>Al corriente</strong>",
          "Hasta <strong>30</strong> días → <strong>1-30 días</strong>",
          "Hasta <strong>60</strong> días → <strong>31-60 días</strong>",
          "Más de <strong>60</strong> días → <strong>Más de 60 días</strong>",
        ],
        "Usa <strong>SI anidados</strong>."
      ),
      solution: [
        `Selecciona ${F("I5")}.`,
        "Un SI anidado pone otro SI en el lugar de \"valor si falso\". Excel evalúa en orden y se detiene en la primera condición verdadera.",
        `Escribe ${F('=SI([@[Días vencida]]=0,"Al corriente",SI([@[Días vencida]]<=30,"1-30 días",SI([@[Días vencida]]<=60,"31-60 días","Más de 60 días")))')}.`,
      ],
      expectedResult: "Al corriente / 1-30 días / 31-60 días / Más de 60 días",
      finalFormula:
        '=SI([@[Días vencida]]=0,"Al corriente",SI([@[Días vencida]]<=30,"1-30 días",SI([@[Días vencida]]<=60,"31-60 días","Más de 60 días")))',
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: ¿Qué hacemos con cada factura?",
      description:
        conditions(
        "En la columna <strong>Acción</strong>, decide qué hacer con cada factura, en este orden:",
        [
          "Si está <strong>pagada</strong> → deja la celda <strong>vacía</strong> (\"\")",
          "Si lleva <strong>más de 60</strong> días vencida → <strong>Enviar a jurídico</strong>",
          "Si lleva <strong>más de 0</strong> días vencida → <strong>Llamar al cliente</strong>",
          "Si todavía no vence → <strong>Enviar recordatorio</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("J5")}.`,
        `Para dejar una celda en blanco, el resultado es ${F('""')} (dos comillas sin nada en medio).`,
        "El orden importa: primero revisa si está pagada, luego el caso más grave.",
        `Escribe ${F('=SI([@Pagada]="Sí","",SI([@[Días vencida]]>60,"Enviar a jurídico",SI([@[Días vencida]]>0,"Llamar al cliente","Enviar recordatorio")))')}.`,
      ],
      expectedResult: "Vacío / Enviar a jurídico / Llamar al cliente / Enviar recordatorio",
      finalFormula:
        '=SI([@Pagada]="Sí","",SI([@[Días vencida]]>60,"Enviar a jurídico",SI([@[Días vencida]]>0,"Llamar al cliente","Enviar recordatorio")))',
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Interés moratorio",
      description:
        conditions(
        "En la columna <strong>Interés moratorio</strong>:",
        [
          "Facturas <strong>no pagadas</strong> → Monto × <strong>tasa mensual (N5)</strong> × Días vencida ÷ 30",
          "Facturas <strong>pagadas</strong> → <strong>0</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("K5")}.`,
        `El cálculo va dentro del \"valor si verdadero\": ${F("[@Monto]*$N$5*[@[Días vencida]]/30")}.`,
        `Escribe ${F('=SI([@Pagada]="No",[@Monto]*$N$5*[@[Días vencida]]/30,0)')}.`,
        "Prueba cambiar la tasa en N5: todos los intereses se recalculan.",
      ],
      expectedResult: "Interés proporcional a los días, o 0",
      finalFormula: '=SI([@Pagada]="No",[@Monto]*$N$5*[@[Días vencida]]/30,0)',
    },
  ],
};
